import { createContext, useContext, useState, useCallback } from "react";
import {
  generateECDHKeyPair,
  exportPublicKey,
  importPublicKey,
  deriveSharedKey,
} from "../utils/cryptoUtils";


export const CryptoContext = createContext({
  encryptionKey: null,
  isReady: false,
  initEncryption: async () => false,
  clearKeys: () => {},
});

export const useCrypto = () => {
  return useContext(CryptoContext);
};

// Función helper para obtener la llave de encriptación de forma síncrona
export const getEncryptionKey = () => {
  if (window.__cryptoContext?.encryptionKey) {
    return window.__cryptoContext.encryptionKey;
  }
  return null;
};


let initializationPromise = null;

export const getOrInitEncryptionKey = async () => {
  let encryptionKey = getEncryptionKey();

  if (encryptionKey) {
    return encryptionKey;
  }

  // Si ya hay una inicialización en progreso, esperar a que termine
  if (initializationPromise) {
    await initializationPromise;
    encryptionKey = getEncryptionKey();
    if (encryptionKey) {
      return encryptionKey;
    }
  }

  if (window.__cryptoContext?.initEncryption) {
    try {
      initializationPromise = window.__cryptoContext.initEncryption();
      const success = await initializationPromise;

      if (success) {
        encryptionKey = getEncryptionKey();
        initializationPromise = null;
        return encryptionKey;
      } else {
        initializationPromise = null; 
        throw new Error("Falló la reinicialización de encriptación");
      }
    } catch (error) {
      initializationPromise = null; 
      console.error("[ECDH] Error al reinicializar:", error);
      throw new Error("No se pudo reinicializar la encriptación ECDH. Inicie sesión nuevamente.");
    }
  }

  throw new Error("Contexto de encriptación no disponible. Inicie sesión nuevamente.");
};


export const useProvideCrypto = () => {
  const [cryptoState, setCryptoState] = useState({
    encryptionKey: null, 
    isReady: false,
  });

  /*Inicializa las llaves de encriptación usando ECDH*/
  const initEncryption = useCallback(async () => {
    try {
      const keyPair = await generateECDHKeyPair();
      const publicKeyBase64 = await exportPublicKey(keyPair.publicKey);

      const response = await fetch(
        `${process.env.REACT_APP_URL_IAM_PDP}/auth/init-encryption`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ publicKey: publicKeyBase64 }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error del servidor: ${response.status} - ${errorText}`);
      }
      const { publicKey: backendPublicKey } = await response.json();

      const serverPublicKey = await importPublicKey(backendPublicKey);

      const sharedKey = await deriveSharedKey(
        keyPair.privateKey,
        serverPublicKey
      );

      const newState = {
        encryptionKey: sharedKey,
        isReady: true,
      };

      setCryptoState(newState);

      if (window.__cryptoContext) {
        window.__cryptoContext.encryptionKey = sharedKey;
        window.__cryptoContext.isReady = true;
      }

      return true;
    } catch (error) {
      console.error("[ECDH] ❌ Error inicializando encriptación:", error);
      setCryptoState({
        encryptionKey: null,
        isReady: false,
      });

      if (window.__cryptoContext) {
        window.__cryptoContext.encryptionKey = null;
        window.__cryptoContext.isReady = false;
      }

      return false;
    }
  }, []);

  /* Limpia las llaves de la memoria*/
  const clearKeys = useCallback(() => {
    setCryptoState({
      encryptionKey: null,
      isReady: false,
    });

    initializationPromise = null;

    if (window.__cryptoContext) {
      window.__cryptoContext.encryptionKey = null;
      window.__cryptoContext.isReady = false;
    }
  }, []);

  return {
    ...cryptoState,
    initEncryption,
    clearKeys,
  };
};
